import React from 'react';
import type { CreatorLookupStatus, SnapshotMetrics } from '@discovr/types';
import { cn } from '../../../lib/utils';
import { formatCompact } from '../../../lib/format';
import { SimpleTooltip } from '../../../components/ui/tooltip';
import { MetricsCaptionText } from './MetricsCaptionText';
import { fetchedAtLabel, resolveMetricsCaption, rowMetrics, TONE_CLASS } from './creatorMetricsStatus';
import { useIsRechecking } from './profileRecheckStore';

interface AvgViewsCellProps {
  m: SnapshotMetrics;
  /** Admin-only extras: the mean beneath the headline, the thin-sample warning,
   *  and the range/sample-size tooltip. A staged rollout, not a confidentiality
   *  boundary — every field is in the roster payload for both roles; brands are
   *  simply not shown the extra detail until the team signs off on it. The
   *  headline number itself is identical on both sides. */
  detailed?: boolean;
  lookup?: CreatorLookupStatus;
  /** Epoch ms from the caller's clock (useNow). Required, not defaulted: a
   *  per-render `new Date()` hidden in a default would defeat that clock. */
  now: number;
  /** The ⟳, already gated by the caller (admin + capability + showMetricsRecheck). */
  recheck?: React.ReactNode;
  lastError?: string;
  /** Row identity for the metrics-recheck store subscription — lets the
   *  caption reflect a re-check still settling server-side. Optional: only
   *  admin/lookup rows ever have a live recheck to settle. */
  campaignId?: string;
  entryId?: string;
}

/** Headline = `avg_views`, rendered as-is whatever its source (a hand-supplied
 *  value, or the reel median the metrics job wrote there). When the views were
 *  fetched the plain MEAN rides along as `mean_views` and sits beneath in
 *  small type, labelled — a manual row has no mean, so nothing is shown.
 *
 *  Values arrive as whole counts; the K/M abbreviation is display-only. */
export function AvgViewsCell({ m, detailed = false, lookup, now, recheck, lastError, campaignId, entryId }: AvgViewsCellProps) {
  const settling = useIsRechecking(campaignId ?? '', entryId ?? '', 'metrics', rowMetrics(m));
  const headline = m.avg_views ?? 0;
  const mean = m.mean_views ?? 0;
  const caption = detailed ? resolveMetricsCaption(m, lookup, now, settling) : null;

  // Brand / public: value or dash, nothing else.
  if (!detailed) {
    if (headline <= 0) return <span className="font-medium text-foreground">—</span>;
    return (
      <span className="font-medium text-foreground" title={headline.toLocaleString()}>
        {formatCompact(headline)}
      </span>
    );
  }

  // Admin with no value: the status caption (or a dash when the profile is
  // not active — the Creator-column ⟳ governs there), plus the ⟳ slot.
  if (headline <= 0) {
    return (
      <span className="inline-flex items-center justify-end gap-1">
        {caption && caption.kind !== 'manual' ? (
          <MetricsCaptionText caption={caption} title={caption.kind === 'failed' ? lastError : undefined} />
        ) : (
          <span className="font-medium text-foreground">—</span>
        )}
        {recheck}
      </span>
    );
  }

  // Admin with a value: existing breakdown tooltip + optional ⟳ sibling.
  const showMean = mean > 0;
  // Explicit numeric checks, and `bound` rather than formatCompact, so a
  // measured floor of 0 reads as a real bound instead of formatCompact's "—"
  // no-data dash. The API drops a 0 bound today (`omitempty`), so this is
  // defensive rather than load-bearing.
  const hasRange =
    typeof m.min_views === 'number' && typeof m.max_views === 'number' && m.max_views > m.min_views;
  const bound = (v: number) => (v === 0 ? '0' : formatCompact(v));

  const breakdown = [
    `${mean > 0 ? 'Typical (median)' : 'Avg views'}: ${headline.toLocaleString()}`,
    mean > 0 ? `Mean: ${mean.toLocaleString()}` : null,
    hasRange ? `Range: ${bound(m.min_views as number)}–${bound(m.max_views as number)}` : null,
    m.view_sample ? `Based on ${m.view_sample} reel${m.view_sample === 1 ? '' : 's'}` : null,
    m.photo_led ? 'Photo-led profile — too few reels to read views reliably' : null,
    fetchedAtLabel(m),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <span className="inline-flex items-center justify-end gap-1">
      <SimpleTooltip content={breakdown}>
        {/* tabIndex, not a bare title: the breakdown carries several data points
            including a reliability warning, and a non-focusable span puts all of
            it out of reach of keyboard users and never fires Radix's
            focus-triggered open. No aria-label — Radix already exposes the open
            content via aria-describedby, and naming the trigger with the same
            string would read it twice. */}
        <span
          tabIndex={0}
          className="inline-flex flex-col items-end rounded-sm leading-tight focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          <span className="font-medium text-foreground">{formatCompact(headline)}</span>
          {/* A photo-led profile has fewer than three measurable reels, so the mean
              and the median are all but the same number — the useful signal there
              is that the figure is thin, not what the mean happens to be. */}
          {m.photo_led ? (
            <span className={cn('text-caption-regular', TONE_CLASS.amber)}>photo-led</span>
          ) : caption?.kind === 'manual' ? (
            <MetricsCaptionText caption={caption} />
          ) : (
            showMean && (
              <span className="text-caption-regular text-muted-foreground">{`mean ${formatCompact(mean)}`}</span>
            )
          )}
        </span>
      </SimpleTooltip>
      {recheck}
    </span>
  );
}
